/*
 *   File : edit-profile.js
 *   Author : https://evoqins.com
 *   Description : File to edit basic details
 *   Integrations : 
 *   Version : 1.0.0
*/

import { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useDispatch, useSelector } from 'react-redux';
import Cookies from "js-cookie";
import { toast } from "react-toastify";
import { Modal } from "bootstrap";

// Components
import { PrimaryButton, TextButton } from "../../Component/Buttons";
import { CustomPhoneInput, CustomTextInput, OTPInput } from "../../Component/FormElements";
import { Icon } from "../../Component/Icon";
import { ProfileCard } from "../../Component/Cards";
import { APIService } from "../../Service";
import { EditProfileStatusModal } from "../../Component/Modal";
import { _scrollToTop } from "../../Helper";


function EditProfile() {

    const USER_DATA = useSelector((store) => store.Reducer.USER_DATA);
    const navigate = useNavigate();
    const dispatch = useDispatch();
    const inputRef = useRef(null);

    const [email, setEmail] = useState(USER_DATA.email);
    const [emailError, setEmailError] = useState("");
    const [number, setNumber] = useState(USER_DATA.mobile);
    const [numberError, setNumberError] = useState("");

    const [enableEdit, setEnableEdit] = useState(false);
    const [editType, setEditType] = useState("");
    const [currentStep, setCurrentStep] = useState(1);

    // Otp 
    const [otpValue, setOtpValue] = useState("");
    const [otpValueError, setOtpValueError] = useState("");
    const [focusedInput, setFocusedInput] = useState(2);
    const [otpTimer, setOTPTimer] = useState(30);
    const [disableResend, setDisabledResend] = useState(false);
    const [loading, setLoading] = useState(false);
    const [showEditProfileStatusModal, setShowEditProfileStatusModal] = useState(false);

    useEffect(() => {
        // Scroll the page to top
        _scrollToTop();
    }, []);

    // OTP timer
    useEffect(() => {
        if (otpTimer !== 0 && currentStep === 2) {
            setTimeout(() => {
                setOTPTimer(timer => timer - 1);
            }, 1000);
        }
    }, [otpTimer, currentStep]);

    useEffect(() => {
        if (showEditProfileStatusModal === true) {
            const edit_profile_modal = new Modal(document.getElementById('edit-profile-modal'));
            edit_profile_modal.show();
        }
    }, [showEditProfileStatusModal]);

    // handle email input change
    const _handleEmailChange = (value) => {
        setEmail(value);
        setEmailError("");
    };

    // handle phone input change
    const _handlePhoneChange = (value) => {
        if (value.length <= 10) {
            setNumber(value);
            setNumberError("");
        }
    };

    // handle edit inputs
    const _handleEditClick = (type) => {
        setEnableEdit(true);
        // type : 1 => Email | Phone
        setEditType(type);

        if (type == 1) {
            setEmail("");
        } else {
            setNumber("");
        }
    };

    // handle validate input and trigger OTP
    const _handleGetOtp = () => {

        let validate_email = /^[a-zA-Z0-9._:$!%-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]+$/;
        let validate_number = /^[6-9]\d{9}$/;

        // type - 1 => Email edit, type - 2 => Phone edit, 
        if (editType == 1) {
            if (email.trim().length === 0) {
                setEmailError("Enter your email");
            } else if (!validate_email.test(email)) {
                setEmailError("Enter a valid email address");
            } else {
                // API Call
                setLoading(true);
                setDisabledResend(true);
                _getEditEmailOTP();
            }
        } else {
            if (number.trim().length === 0) {
                setNumberError("Enter the phone number");
            } else if (!validate_number.test(number)) {
                setNumberError("Enter a valid phone number");
            } else {
                setLoading(true);
                setDisabledResend(true);
                // API Call
                _getEditPhoneOTP();
            }
        }


        // if (is_valid === true) {
        //     setCurrentStep(2);
        //     setFocusedInput(2);
        // }


    };

    // handle cancel edit
    const _handleCancelEdit = () => {

        if (currentStep == 2) {
            setOtpValue("");
            setOtpValueError("");
            setOTPTimer(30);
        } else {
            setEnableEdit(false);
            setEditType("");
            setNumberError("");
            setEmailError("");
        }
        setCurrentStep(1);

        setEmail(USER_DATA.email);
        setNumber(USER_DATA.mobile);
    };

    //handle Otp change
    const _handleChangeOTP = (value) => {
        setOtpValue(value);
        setOtpValueError("");
    };

    // handle resend OTP 
    const _handleResendOtp = () => {
        setLoading(true);
        setDisabledResend(true);
        if (editType == 2) {
            _getEditPhoneOTP("resend");
        } else {
            _getEditEmailOTP("resend");
        }
    };

    // handle verify OTP 
    const _handleVerifyOTP = () => {
        if (otpValue.length < 4) {
            setOtpValueError("Please provide received otp");
            setFocusedInput(2);
            return;
        } else {
            setLoading(true);
            if (editType == 2) { // Phone
                // API call
                _verifyPhoneOTP();
            } else {
                _verifyEmailOTP();
            }
        }
    };



    // ===================================== //
    // ============ API CALLS ============== //

    // Get Edit phone OTP
    const _getEditPhoneOTP = async (type) => {
        let url = "profile/edit-mobile";
        let data = {
            "mobile": Number(number),
            "user_id": USER_DATA.unique_identifier
        };

        const response = await APIService(url, data);

        if (response.status_code === 200) {
            if (type === "resend") {
                toast.dismiss();
                toast.success('OTP re-sent successfully');
            } else {
                toast.dismiss();
                toast.success('OTP sent to entered Phone Number');
            }
            setOTPTimer(30);
            setCurrentStep(2);
            setFocusedInput(2);
        } else {
            toast.dismiss();
            toast.error(response.message);
        }
        setOtpValue("");
        setLoading(false);
        setDisabledResend(false);
    };

    // Verify phone OTP
    const _verifyPhoneOTP = async () => {
        let url = "profile/validate-mobile";
        let data = {
            "mobile": Number(number),
            "otp": Number(otpValue),
            "user_id": USER_DATA.unique_identifier
        };

        const response = await APIService(url, data);

        if (response.status_code === 200) {
            setShowEditProfileStatusModal(true);
            // Updating profile - API
            _updateProfile();

        } else {
            setFocusedInput(2);
            setOtpValue("");
            toast.dismiss();
            toast.error(response.message);
        }

        setLoading(false);
    };

    // Get Edit phone OTP
    const _getEditEmailOTP = async (type) => {
        let url = "profile/edit-email";
        let data = {
            "email": email,
            "user_id": USER_DATA.unique_identifier
        };

        const response = await APIService(url, data);

        if (response.status_code === 200) {
            if (type === "resend") {
                toast.dismiss();
                toast.success('OTP re-sent successfully');
            } else {
                toast.dismiss();
                toast.success('OTP sent to entered Email ID');
            }
            setOTPTimer(30);
            setCurrentStep(2);
            setFocusedInput(2);
        } else {
            toast.dismiss();
            toast.error(response.message);
        }
        setOtpValue("");
        setLoading(false);
        setDisabledResend(false);
    };

    // Verify email OTP
    const _verifyEmailOTP = async () => {
        let url = "profile/validate-email";
        let data = {
            "email": email,
            "otp": Number(otpValue),
            "user_id": USER_DATA.unique_identifier
        };

        const response = await APIService(url, data);

        if (response.status_code === 200) {
            setShowEditProfileStatusModal(true);
            // Updating profile - API
            _updateProfile();
        } else {
            setFocusedInput(2);
            setOtpValue("");
            toast.dismiss();
            toast.error(response.message);
        }

        setLoading(false);
    };

    // Get Profile data
    const _updateProfile = async () => {

        let url = "profile/get";
        let data = {
            "user_id": USER_DATA.unique_identifier
        };

        const response = await APIService(url, data);

        if (response && response.status_code === 200) {
            dispatch({ type: "UPDATE_USER_DATA", payload: response.data });
        } else {
            let options = { domain: process.env.REACT_APP_DOMAIN, path: '/' };
            dispatch({ type: "CLEAR_STORE" });
            Cookies.remove("indvestment_access_token", options);
            Cookies.remove("indvestment_user_id", options);
            toast.dismiss();
            toast.error(response.message);
        }
    };


    return (
        <div className="row mb-5">
            <div className="col-12">
                <TextButton name="Edit Profile"
                    icon={true}
                    type={2}
                    onPress={() => navigate(-1)} />
            </div>

            <div className="row mt-4 pe-0 flex-column flex-md-row gap-3 gap-md-0">
                <div className="order-2 order-md-1 col-12 col-md-7 pe-0 pe-sm-auto">
                    <div className="p-3 card-box-shadow e-bg-white min-vh-100">

                        {
                            enableEdit &&
                            <Icon icon="left-arrow"
                                className="cursor-pointer mb-2"
                                size="18px"
                                color="#523FC0"
                                onClick={_handleCancelEdit} />
                        }

                        {
                            currentStep === 1 ?
                                <>


                                    {/* user email */}
                                    {
                                        editType !== 2 &&
                                        <CustomTextInput label="Email"
                                            placeholder="Enter your email address"
                                            type="text"
                                            require
                                            value={email}
                                            error={emailError}
                                            edit={enableEdit ? false : true}
                                            onEdit={() => _handleEditClick(1)}
                                            readOnly={enableEdit ? false : true}
                                            wrapperClass={`${enableEdit ? "" : "pb-3"}  col-sm-10`}
                                            className="w-100"
                                            handleChange={_handleEmailChange} />
                                    }

                                    {/* Phone number */}
                                    {
                                        editType !== 1 &&
                                        <CustomPhoneInput label="Phone number"
                                            placeholder="Enter your phone number"
                                            type="text"
                                            require
                                            autoFocus={false}
                                            value={number}
                                            error={numberError}
                                            edit={enableEdit ? false : true}
                                            onEdit={() => _handleEditClick(2)}
                                            readOnly={enableEdit ? false : true}
                                            className="w-100"
                                            wrapperClass="col-sm-10"
                                            handleChange={_handlePhoneChange} />
                                    }

                                    {
                                        enableEdit &&
                                        <PrimaryButton name="Get OTP"
                                            fontSize="e-font-16"
                                            status={loading}
                                            disabled={loading}
                                            className="mt-4 col-12 col-sm-10 h-48"
                                            onPress={_handleGetOtp} />
                                    }

                                </>
                                :
                                <>

                                    <OTPInput label="Enter OTP"
                                        require
                                        ref={inputRef}
                                        value={otpValue}
                                        spellCheck="false"
                                        error={otpValueError}
                                        focused_input={focusedInput}
                                        onFocus={() => setFocusedInput(2)}
                                        onBlur={() => setFocusedInput(0)}
                                        onChange={_handleChangeOTP} />
                                    <p className='d-flex flex-wrap gap-1 mb-3 mt-4 e-font-14 e-alt-font-lato line-height-20px e-text-chinese-black e-font-weight-400'>
                                        Didn’t receive code yet?
                                        <span className={`${disableResend || otpTimer !== 0 ? "pe-none opacity-75" : ""} text-nowrap e-text-primary-color e-font-weight-600 cursor-pointer text-decoration-hover-underline`}
                                            onClick={_handleResendOtp}>
                                            Resend OTP
                                            {
                                                otpTimer !== 0 && ` in ${otpTimer} ${otpTimer > 1 ? "seconds" : "second"}`
                                            }
                                        </span>
                                    </p>

                                    <PrimaryButton name="Continue"
                                        fontSize="e-font-16"
                                        className="col-12 col-sm-10 h-48"
                                        status={loading}
                                        disabled={loading}
                                        // className="w-100 h-48"
                                        onPress={_handleVerifyOTP} />
                                </>
                        }

                    </div>
                </div>
                <div className="order-1 order-md-2 col-12 col-sm-8 col-md-5 pe-0">
                    <ProfileCard navigate={true} />
                </div>
            </div>


            {/* Edit profile status modal */}
            {
                showEditProfileStatusModal === true &&
                <EditProfileStatusModal
                    type={editType}
                    handleSuccess={() => navigate("/account")}
                    handleClose={() => setShowEditProfileStatusModal(false)} />
            }

        </div>
    )
}

export default EditProfile;